import { useState } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  Caption1,
  Divider,
  Text,
  Title3,
  makeStyles,
  mergeClasses,
  shorthands,
} from '@fluentui/react-components';
import type { ChecklistItem, PersonChecklist } from '@vigeo/shared';
import { complianceTokenFor, shellMedia, shellMetrics, tokens } from '@vigeo/ui';
import { ReadinessBadge } from './ReadinessBadge.js';
import { RecordRequirementTrigger, RecordRequirementWhenOpen } from './RecordRequirement.js';
import { RecordingRowProvider, useRecording } from './RecordingRowProvider.js';
import { RequestUploadLink } from './RequestUploadLink.js';
import { bandOf, bandPresenters, checklistBands, statePresenters, statusPresenters, timingPresenters } from './presenters.js';

const useStyles = makeStyles({
  /** The row whose form is open, marked in the page as well as by the badge beside its label. */
  rowRecording: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  view: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  header: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  // 2.75rem is the accessible minimum touch target on a phone (Gate 6); desktop keeps Fluent's height.
  bandHeader: { [shellMedia.belowTablet]: { minHeight: '2.75rem' } },
  headline: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  rows: { display: 'flex', flexDirection: 'column' },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    alignItems: 'start',
    [shellMedia.phone]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  rowMain: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  meta: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    alignItems: 'center',
    [shellMedia.phone]: { '& > a, & > button, & > div > button': { minHeight: shellMetrics.touchTarget } },
  },
  /** The item-status voice: light chip, tone as text and edge, a dot so colour is never the only cue. */
  statusChip: { fontWeight: tokens.fontWeightSemibold, borderRadius: tokens.borderRadiusSmall, ...shorthands.borderWidth(tokens.strokeWidthThin), ...shorthands.borderStyle('solid') },
  showAll: { alignSelf: 'flex-start', marginTop: tokens.spacingVerticalS },
  dot: { display: 'inline-block', width: tokens.spacingHorizontalSNudge, height: tokens.spacingHorizontalSNudge, borderRadius: tokens.borderRadiusCircular, backgroundColor: 'currentColor', marginRight: tokens.spacingHorizontalXS },
  bandCount: { marginLeft: tokens.spacingHorizontalS },
});

/** A date the reader can scan, not an ISO string. */
export function formatDue(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** The one rendering of a requirement status — light chip, tone from the presenter. */
export function StatusChip({ status }: { readonly status: ChecklistItem['status'] }) {
  const styles = useStyles();
  const presenter = statusPresenters[status];
  const tone = complianceTokenFor[presenter.tone];
  return (
    <Badge size="small" shape="rounded" appearance="outline" className={styles.statusChip} style={{ color: tone, borderColor: tone }}>
      <span aria-hidden="true" className={styles.dot} />
      {presenter.label}
    </Badge>
  );
}

export function ChecklistRow({
  item,
  personId,
  canRecord,
}: {
  readonly item: ChecklistItem;
  readonly personId: string;
  /** Only admins may close a row; viewers see the status without the control. */
  readonly canRecord: boolean;
}) {
  const styles = useStyles();
  const timing = timingPresenters[item.timing];
  const recording = useRecording();
  const open = recording?.openId === item.requirementKey;

  return (
    <div className={open ? mergeClasses(styles.row, styles.rowRecording) : styles.row}>
      <div className={styles.rowMain}>
        <Text weight="semibold">{item.label}</Text>
        {/* Said in words as well as in colour: the tint alone is invisible to anyone who
            cannot see it, and to anyone reading the row through a screen reader. */}
        {open ? <Badge appearance="filled" color="brand" size="small">Recording</Badge> : null}
        {item.description ? <Caption1>{item.description}</Caption1> : null}
        <div className={styles.meta}>
          {/* Why this row is on this person's list at all. */}
          <Caption1>{item.sources.join(' · ')}</Caption1>
          {item.segregated ? <Badge appearance="outline" size="small">Separate file</Badge> : null}
          {timing.canBeOverdue ? null : <Badge appearance="outline" size="small">{timing.label}</Badge>}
        </div>
        {timing.note === '' ? null : <Caption1>{timing.note}</Caption1>}
        {item.compliance?.documents?.map(document => <a data-document-link key={document.url} href={document.url} target="_blank" rel="noreferrer">{document.label}</a>)}
        {item.compliance?.reviewQuestion ? <Caption1>{item.compliance.reviewQuestion}</Caption1> : null}
        {item.compliance?.firstReviewMonths ? <Caption1>Review owner: {item.compliance.reviewOwner}. First review {item.compliance.firstReviewMonths} calendar months after {item.compliance.firstReviewRequirementKey ? 'the initial education completion' : 'the actual hire date'}; later reviews follow completion.</Caption1> : null}
        {item.openQuestionId === null ? null : <Caption1>Awaiting HR decision ({item.openQuestionId}).</Caption1>}
      </div>
      <div className={styles.meta}>
        {item.dueAt === null ? null : <Caption1>Due {formatDue(item.dueAt)}</Caption1>}
        {/* #232: the person said this document is not ready; HR sends the upload link. */}
        {item.awaitingUploadLinkInstanceId == null ? null : (
          <RequestUploadLink personId={personId} instanceId={item.awaitingUploadLinkInstanceId} canRequest={canRecord} />
        )}
        {/* Only where HR can actually close it, only while it is still open, and only for
            someone allowed to — offering "Record" to a viewer is a request that will fail. */}
        {canRecord && item.directlyRecordable && item.status !== 'satisfied' && item.status !== 'scheduled' ? (
          <RecordRequirementTrigger item={item} />
        ) : null}
        <StatusChip status={item.status} />
      </div>
    </div>
  );
}

export interface ChecklistBandsProps {
  readonly checklist: PersonChecklist;
  readonly canRecord: boolean;
  readonly startCollapsed?: boolean;
}

/** Rows the person or HR still has to act on stay visible; met rows fold away. */
const settled: ReadonlySet<ChecklistItem['status']> = new Set(['satisfied', 'not_applicable']);

function Band({
  items,
  personId,
  canRecord,
}: {
  readonly items: readonly ChecklistItem[];
  readonly personId: string;
  readonly canRecord: boolean;
}) {
  const styles = useStyles();
  const [showAll, setShowAll] = useState(false);
  const open = items.filter((item) => !settled.has(item.status));
  const done = items.filter((item) => settled.has(item.status));
  const visible = showAll ? items : open;
  return (
    <>
      <div className={styles.rows}>
        {visible.map((item, index) => (
          <div key={item.requirementKey}>
            {index === 0 ? null : <Divider />}
            <ChecklistRow item={item} personId={personId} canRecord={canRecord} />
            {/* UNDER the row and outside it, so the form does not have to fit the row's own
                two-column layout (PG15). Mounted only while open, so each row starts clean. */}
            {canRecord && item.directlyRecordable ? <RecordRequirementWhenOpen item={item} personId={personId} /> : null}
          </div>
        ))}
        {visible.length === 0 ? <Caption1>Nothing outstanding in this band.</Caption1> : null}
      </div>
      {done.length > 0 ? (
        <Button appearance="subtle" size="small" className={styles.showAll} aria-expanded={showAll} onClick={() => setShowAll((v) => !v)}>
          {showAll ? `Hide ${done.length} complete` : `Show all ${items.length} (${done.length} complete)`}
        </Button>
      ) : null}
    </>
  );
}

/**
 * The three bands. Grouping comes from DATA (`owner`, `gates`) via `bandOf` — never from a
 * requirement key. The blocking band is open by default because it is the question HR
 * opened the page to answer; the rest starts collapsed so 75 rows do not bury it. Within a
 * band, completed rows fold behind "Show all" so what is outstanding is the first thing seen.
 */
export function ChecklistBands({ checklist, canRecord, startCollapsed = false }: ChecklistBandsProps) {
  const styles = useStyles();
  const banded = checklistBands.map((band) => ({
    band,
    presenter: bandPresenters[band],
    items: checklist.items.filter((item) => bandOf(item) === band),
  }));

  return (
    <Accordion multiple collapsible defaultOpenItems={startCollapsed ? [] : banded.filter((b) => b.presenter.defaultOpen).map((b) => b.band)}>
      {banded.map(({ band, presenter, items }) => {
        const open = items.filter((item) => !settled.has(item.status)).length;
        return (
          <AccordionItem key={band} value={band}>
            <AccordionHeader className={styles.bandHeader}>
              <Text weight="semibold">{presenter.label}</Text>
              <Badge appearance="tint" size="small" className={styles.bandCount}>
                {open === 0 ? `${items.length}` : `${open} open · ${items.length}`}
              </Badge>
            </AccordionHeader>
            <AccordionPanel>
              <Caption1>{presenter.description}</Caption1>
              <Band items={items} personId={checklist.personId} canRecord={canRecord} />
            </AccordionPanel>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

export interface ChecklistViewProps {
  readonly checklist: PersonChecklist;
  readonly canRecord?: boolean;
}

/** Verdict + progress + bands in one block, for surfaces without the full overview. */
export function ChecklistView({ checklist, canRecord = false }: ChecklistViewProps) {
  const styles = useStyles();
  const state = statePresenters[checklist.state];
  const { met, total, unscorable } = checklist.progress;

  return (
    /* ONE provider above every band, not one per band: "only one form open at a time" is a
       statement about the whole checklist, and a provider per band would allow one open in each. */
    <RecordingRowProvider>
    <div className={styles.view}>
      <div className={styles.header}>
        <div className={styles.headline}>
          <Title3>Onboarding checklist</Title3>
          <ReadinessBadge state={checklist.state} />
        </div>
        <Text>{state.meaning}</Text>
        {state.nextStep === '' ? null : <Text weight="semibold">{state.nextStep}</Text>}
        {/*
          * "0 of 0 complete" is the same false positive as the verdict, in a different
          * sentence: a fraction whose denominator is zero reads as a finished checklist
          * (known-limitations #41). With nothing scorable there is nothing to report but that.
          */}
        <Text weight="semibold">
          {total - unscorable === 0 ? 'Nothing to complete yet' : `${met} of ${total - unscorable} complete`}
        </Text>
        {unscorable > 0 ? <Caption1>{unscorable} more cannot be scored until HR sets their interval.</Caption1> : null}
      </div>
      <ChecklistBands checklist={checklist} canRecord={canRecord} />
    </div>
    </RecordingRowProvider>
  );
}
